import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { getContentfulData } from '../firebase';

// Define types based on the Contentful data structure
interface ContentfulSys {
  id: string;
  type: string;
  contentType?: {
    sys: {
      id: string;
      type: string;
    };
  };
}

interface ContentfulAsset {
  sys: ContentfulSys;
  fields: {
    title: string;
    description?: string;
    file: {
      url: string;
      details: {
        size: number;
        image?: {
          width: number;
          height: number;
        };
      };
      fileName: string;
      contentType: string;
    };
  };
}

interface ContentfulEntry {
  sys: ContentfulSys;
  fields: Record<string, any>;
}

interface ContentfulResponse {
  sys: {
    type: string;
  };
  total: number;
  skip: number;
  limit: number;
  items: ContentfulEntry[];
  includes?: {
    Asset?: ContentfulAsset[];
  };
  allAssets?: ContentfulAsset[];
}

// Define the context type
interface ContentContextType {
  loading: boolean;
  error: string | null;
  content: ContentfulResponse | null;

  // Helper functions
  getEntriesByType: (contentType: string) => ContentfulEntry[];
  getEntryById: (id: string) => ContentfulEntry | null;
  getEntryByField: (
    contentType: string,
    fieldName: string,
    fieldValue: any
  ) => ContentfulEntry | null;
  getAssetById: (id: string) => ContentfulAsset | null;
  getAssetByTitle: (title: string) => ContentfulAsset | null;
  getUiText: (id: string) => string | null;
  getUiLink: (id: string) => { url: string; title: string } | null;
  getUiImage: (id: string) => ContentfulAsset | null;
  getPageById: (id: string) => ContentfulEntry | null;
  getAllPages: () => ContentfulEntry[];
  getProduct: (id: string) => ContentfulEntry | null;
  refreshContent: () => Promise<void>;
}

// Create the context with default values
export const ContentContext = createContext<ContentContextType>({
  loading: true,
  error: null,
  content: null,
  getEntriesByType: () => [],
  getEntryById: () => null,
  getEntryByField: () => null,
  getAssetById: () => null,
  getAssetByTitle: () => null,
  getUiText: () => null,
  getUiLink: () => null,
  getUiImage: () => null,
  getPageById: () => null,
  getAllPages: () => [],
  getProduct: () => null,
  refreshContent: async () => {},
});

export const ContentProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [content, setContent] = useState<ContentfulResponse | null>(null);

  const fetchContent = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getContentfulData();
      if (!result.data) {
        console.error('❌ No data in result:', result);
        setError('No data received from server');
        setLoading(false);
        return;
      }

      const contentfulData = result.data as ContentfulResponse;

      setContent(contentfulData);
      setLoading(false);
    } catch (err) {
      setError('Failed to load content. Please try again later.');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  // Helper to get entries by content type
  const getEntriesByType = (contentType: string): ContentfulEntry[] => {
    if (!content?.items) return [];

    return content.items.filter(
      (entry) => entry.sys.contentType?.sys.id === contentType
    );
  };

  // Helper to get a specific entry by ID
  const getEntryById = (id: string): ContentfulEntry | null => {
    if (!content?.items) return null;

    return content.items.find((entry) => entry.sys.id === id) || null;
  };

  // Helper to get an entry by field value
  const getEntryByField = (
    contentType: string,
    fieldName: string,
    fieldValue: any
  ): ContentfulEntry | null => {
    if (!content?.items) return null;

    return (
      content.items.find(
        (entry) =>
          entry.sys.contentType?.sys.id === contentType &&
          entry.fields[fieldName] === fieldValue
      ) || null
    );
  };

  // Helper to get an asset by ID from the includes.Asset array
  const getAssetById = (id: string): ContentfulAsset | null => {
    if (!content?.includes?.Asset) return null;

    return content.includes.Asset.find((asset) => asset.sys.id === id) || null;
  };

  // Helper to get an asset by its Contentful title field
  const getAssetByTitle = (title: string): ContentfulAsset | null => {
    const pool = [
      ...(content?.allAssets ?? []),
      ...(content?.includes?.Asset ?? []),
    ];
    return pool.find((asset) => asset.fields.title === title) ?? null;
  };

  // Helper to get UI text by ID
  const getUiText = (id: string): string | null => {
    const uiTextEntry = getEntryByField('uiText', 'id', id);
    return uiTextEntry?.fields.content || null;
  };

  // Helper to get UI link by ID
  const getUiLink = (id: string): { url: string; title: string } | null => {
    const uiLinkEntry = getEntryByField('uiLink', 'id', id);
    if (!uiLinkEntry) {
      return null;
    }
    const url =
      typeof uiLinkEntry.fields.url === 'string' ? uiLinkEntry.fields.url : '';
    const title =
      typeof uiLinkEntry.fields.title === 'string'
        ? uiLinkEntry.fields.title
        : '';
    return { url, title };
  };

  // Helper to get an image asset by a uiImage entry's ID field
  const getUiImage = (id: string): ContentfulAsset | null => {
    const uiImageEntry = getEntryByField('uiImage', 'id', id);
    const assetId = uiImageEntry?.fields.image?.sys?.id;
    return assetId ? getAssetById(assetId) : null;
  };

  // Helper to get a page by its ID field
  const getPageById = (id: string): ContentfulEntry | null => {
    return getEntryByField('page', 'id', id);
  };

  // Helper to get all pages
  const getAllPages = (): ContentfulEntry[] => {
    return getEntriesByType('page');
  };

  // Helper to get a product by its ID field
  const getProduct = (id: string): ContentfulEntry | null => {
    return getEntryByField('product', 'id', id);
  };

  // Function to manually refresh content
  const refreshContent = async (): Promise<void> => {
    await fetchContent();
  };

  return (
    <ContentContext.Provider
      value={{
        loading,
        error,
        content,
        getEntriesByType,
        getEntryById,
        getEntryByField,
        getAssetById,
        getAssetByTitle,
        getUiText,
        getUiLink,
        getUiImage,
        getPageById,
        getAllPages,
        getProduct,
        refreshContent,
      }}>
      {children}
    </ContentContext.Provider>
  );
};

// Custom hook to use the ContentContext
export const useContentful = () => {
  return React.useContext(ContentContext);
};
